import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TemplateSelection, type Template } from './TemplateSelection';
import { BasicDetails } from './BasicDetails';
import { ReviewCreate } from './ReviewCreate';
import { useUser } from '@shared/hooks/useUser';

interface SimpleWizardContainerProps {
  onSuccess?: () => void;
}

interface WizardData {
  name: string;
  agent_id: string;
  host?: string;
  port?: number;
  username?: string;
  password?: string;
  database?: string;
  path?: string;
  useVSS?: boolean;
}

const INITIAL_DATA: WizardData = {
  name: '',
  agent_id: '',
};

export function SimpleWizardContainer({ onSuccess }: SimpleWizardContainerProps) {
  const { t } = useTranslation('backups');
  const { user } = useUser();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [data, setData] = useState<WizardData>(INITIAL_DATA);
  const [isNameCustomized, setIsNameCustomized] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userTimezone, setUserTimezone] = useState<string>('UTC');

  // Get user's timezone from profile
  useEffect(() => {
    if (user?.timezone) {
      setUserTimezone(user.timezone);
    }
  }, [user]);

  useEffect(() => {
    const mainContent = document.getElementById('main-content');
    if (mainContent) mainContent.scrollTop = 0;
  }, [step]);

  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template);
    setError(null);
    if (!isNameCustomized) {
      setData((prev) => ({
        ...prev,
        name: t(`simpleWizard.template.items.${template.id}.name`),
      }));
    }
  };

  const handleDataChange = (newData: Record<string, unknown>) => {
    if (Object.prototype.hasOwnProperty.call(newData, 'name')) {
      setIsNameCustomized(true);
    }
    setData((prev) => ({ ...prev, ...newData }));
    setError(null);
  };

  const handleCreate = async () => {
    if (!selectedTemplate) return;

    setIsCreating(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');

      // Fetch default PLATFORM storage
      const storageRes = await fetch('/api/v1/storage', {
        headers: { Authorization: `Bearer ${token}` },
      });
      let storage_destination_id = '';
      if (storageRes.ok) {
        const storageData = await storageRes.json();
        const destinations = storageData.destinations || storageData;
        const platformStorage = destinations.find((s: { type: string }) => s.type === 'PLATFORM');
        if (platformStorage) {
          storage_destination_id = platformStorage.id;
        }
      }
      if (!storage_destination_id) {
        setError(t('storage.platformNotAvailable'));
        setIsCreating(false);
        return;
      }

      const payload = {
        name: data.name,
        type: selectedTemplate.type,
        agent_id: data.agent_id,
        storage_destination_id: storage_destination_id,
        run_missing_jobs_on_startup: false,
        retention_policy: {
          keep_daily: selectedTemplate.retentionDaily,
          keep_weekly: selectedTemplate.retentionWeekly,
          keep_monthly: selectedTemplate.retentionMonthly,
          keep_last: selectedTemplate.retentionLast,
        },
        config:
          selectedTemplate.type === 'POSTGRESQL'
            ? {
                host: data.host,
                port: data.port,
                username: data.username,
                password: data.password,
                database: data.database,
                databases: data.database ? [data.database] : [],
                use_pg_dump_all: false,
                backup_globals_only: false,
              }
            : {
                paths: data.path ? [data.path] : [],
                vss: {
                  enabled: data.useVSS ?? true,
                },
              },
        schedule: {
          type: selectedTemplate.scheduleType === 'CUSTOM' ? 'CRON' : selectedTemplate.scheduleType,
          timezone: userTimezone,
          ...(selectedTemplate.scheduleType === 'CUSTOM' && {
            cron_expression: selectedTemplate.cronExpression,
          }),
        },
      };

      const res = await fetch('/api/v1/backups/configs', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const configData = await res.json();
        const configId = configData.config?.id || configData.id;

        const alertRes = await fetch(`/api/v1/backups/configs/${configId}/alert-settings`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            grace_period_minutes: selectedTemplate.alertGracePeriod,
            throttle_interval_minutes: selectedTemplate.alertThrottle,
          }),
        });

        if (!alertRes.ok) {
          console.error('Failed to save alert settings:', await alertRes.text());
        }

        onSuccess?.();
      } else {
        const err = await res.json();
        setError(err.message || t('simpleWizard.errors.createFailed'));
      }
    } catch (_e) {
      setError(t('simpleWizard.errors.networkError'));
    } finally {
      setIsCreating(false);
    }
  };

  const steps = [
    { id: 1, label: t('simpleWizard.steps.template') },
    { id: 2, label: t('simpleWizard.steps.details') },
    { id: 3, label: t('simpleWizard.steps.review') },
  ];

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-center space-x-2">
          {steps.map((s, index) => (
            <div key={s.id} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                  step >= s.id
                    ? 'bg-brand-600 text-white'
                    : 'bg-surface-border-light dark:bg-surface-border-dark text-text-muted-light dark:text-text-muted-dark'
                }`}
              >
                {s.id}
              </div>
              <span
                className={`ml-2 text-sm font-medium hidden sm:block ${
                  step >= s.id
                    ? 'text-slate-900 dark:text-white'
                    : 'text-text-muted-light dark:text-text-muted-dark'
                }`}
              >
                {s.label}
              </span>
              {index < steps.length - 1 && (
                <div
                  className={`w-8 sm:w-12 h-0.5 mx-2 sm:mx-4 ${
                    step > s.id
                      ? 'bg-brand-600'
                      : 'bg-surface-border-light dark:bg-surface-border-dark'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div
          role="alert"
          aria-live="assertive"
          className="mb-6 p-4 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-xl"
        >
          <p className="text-sm text-state-danger-light dark:text-state-danger-dark">{error}</p>
        </div>
      )}

      <div className="bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl p-6 shadow-soft">
        {step === 1 && (
          <TemplateSelection
            selectedTemplate={selectedTemplate}
            onSelect={handleTemplateSelect}
            onNext={() => setStep(2)}
          />
        )}

        {step === 2 && selectedTemplate && (
          <BasicDetails
            template={selectedTemplate}
            data={data}
            onChange={handleDataChange}
            onNext={() => setStep(3)}
            onBack={() => setStep(1)}
          />
        )}

        {step === 3 && selectedTemplate && (
          <ReviewCreate
            template={selectedTemplate}
            data={data}
            onCreate={handleCreate}
            onBack={() => setStep(2)}
            isCreating={isCreating}
          />
        )}
      </div>
    </div>
  );
}
